/**
 * AI Configuration
 *
 * Supports two modes:
 * 1. Proxy mode: Use backend server (API Key stored securely)
 * 2. Direct mode: Call API directly (for mobile access when backend is unreachable)
 */

// Get backend URL from environment variable or use default
const getBackendUrl = (): string => {
  // In Vite, environment variables prefixed with VITE_ are available
  // @ts-ignore - Vite injects this at build time
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_BACKEND_URL) {
    // @ts-ignore
    return import.meta.env.VITE_BACKEND_URL;
  }

  // Always use current host (works for both localhost and LAN IP)
  if (typeof window !== 'undefined') {
    return `${window.location.protocol}//${window.location.hostname}:${window.location.port || '3003'}`;
  }

  return 'http://localhost:3003';
};

export const AI_CONFIG = {
  // Backend API URL (change this to your deployed backend URL)
  // Use environment variable if available, otherwise fallback to localhost
  BACKEND_URL: getBackendUrl(),

  // 🔒 Access password for security (简化版，容易输入)
  ACCESS_PASSWORD: 'english2024',

  // Direct API configuration (for mobile access)
  // ⚠️ WARNING: API Key is exposed in frontend - use only for personal/private use
  DIRECT_API_ENABLED: false, // Disabled due to CORS issues
  DIRECT_API_URL: '',
  DIRECT_API_KEY: '',

  // Default model to use
  DEFAULT_MODEL: 'DeepSeek-V4-Pro',

  // Available models list (for UI display only)
  AVAILABLE_MODELS: [
    // DeepSeek 系列
    { name: 'DeepSeek-V4-Pro', group: 'DeepSeek', recommended: true },
    { name: 'DeepSeek-V4-Flash', group: 'DeepSeek', recommended: false },

    // 通义千问系列
    { name: 'qwen3.7', group: 'Qwen', recommended: false },
    { name: 'qwen3.6', group: 'Qwen', recommended: false },
    { name: 'qwen3.5-plus', group: 'Qwen', recommended: false },

    // GLM 系列
    { name: 'glm-5.1', group: 'GLM', recommended: false },
    { name: 'glm-5', group: 'GLM', recommended: false },

    // MiniMax 系列
    { name: 'MiniMax-M3', group: 'MiniMax', recommended: false },
    { name: 'MiniMax-M3-highspeed', group: 'MiniMax', recommended: false },
    { name: 'MiniMax-M2.7', group: 'MiniMax', recommended: false },
    { name: 'MiniMax-M2.7-highspeed', group: 'MiniMax', recommended: false },
    { name: 'MiniMax-M2.5', group: 'MiniMax', recommended: false },
    { name: 'MiniMax-M2.5-highspeed', group: 'MiniMax', recommended: false },

    // Kimi 系列
    { name: 'kimi-k2.6', group: 'Kimi', recommended: false },
    { name: 'kimi-k2.5', group: 'Kimi', recommended: false },
  ],

  // Maximum response length (keep short for language learning)
  MAX_TOKENS: 150,

  // Response temperature (0.7 is natural)
  TEMPERATURE: 0.7,
};

/**
 * Security Note:
 *
 * 🔒 Proxy mode (default):
 *    - API Key is stored on the server (secure)
 *    - Safe for public deployment
 *    - Requires accessible backend server
 *
 * 🔓 Direct mode (for mobile):
 *    - API Key is exposed in frontend code
 *    - Use only for personal/private applications
 *    - Works without backend server
 *    - Recommend: Use limited-access API Key
 *
 * For production:
 * 1. Use proxy mode with HTTPS backend
 * 2. Or use direct mode with IP/usage-limited API Key
 */